import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectCurrentUser } from '../features/auth/authSlice';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { SurveysForm } from '../components/SurveysForm';
import { SurveyActionsDropdown } from '../components/SurveyActionsDropdown';
import { DataTable } from '../components/DataTable';
import { DateCell } from '../components/DateCell';
import { Survey, SurveyPayload, SurveyCategory } from '../features/surveys/types';
import {
  loadSurveys,
  addSurvey,
  editSurvey,
  removeSurvey,
  setSurveysSearch,
  setSurveysCategory,
  setSurveysType,
  setSurveysActive,
  clearCurrentSurvey,
} from '../features/surveys/surveysSlice';
import { generateSurveyToken } from '../features/surveys/surveysApi';
import {
  loadRecipients,
  addRecipient,
  removeRecipient,
  clearRecipients,
  generateAndSend,
} from '../features/surveys/assessmentRecipientsSlice';
import { fetchMyGroupMemberships } from '../features/userGroups/userGroupsApi';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareSurveyDialog } from '../components/ShareSurveyDialog';

const columnHelper = createColumnHelper<Survey>();

const columns = [
  columnHelper.accessor('title', {
    header: 'Título',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('type', {
    header: 'Tipo',
    cell: (info) => {
      const type = info.getValue();
      return type === 'periodic' ? 'Periódica' : 'Permanente';
    },
  }),
  columnHelper.display({
    id: 'permissions',
    header: 'Tu acceso',
    cell: () => null, // Se sobrescribirá en el render con canEditSurvey
  }),
  columnHelper.accessor('active', {
    header: 'Estado',
    cell: (info) => {
      const active = info.getValue();
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {active ? 'Activa' : 'Inactiva'}
        </span>
      );
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Creado',
    cell: (info) => <DateCell value={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Acciones',
    cell: () => null, // Se sobrescribirá más abajo
  }),
];

function SurveyPermissionsCell({ canEdit }: { canEdit: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground" title="Puedes ver respuestas y estadísticas">
        Ver
      </span>
      {canEdit && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/15 text-primary" title="Puedes editar y gestionar esta encuesta">
          Editar
        </span>
      )}
    </span>
  );
}

// ShareSurveyDialog is now imported from ../components/ShareSurveyDialog
// (old inline version removed - see new component for full functionality)

function canUserEditSurvey(
  survey: Survey,
  user: { role?: string; id?: number } | null,
  myGroupIds: string[]
): boolean {
  if (user?.role === 'superadmin' || user?.role === 'admin') return true;
  if (!survey.createdById || !user?.id) return false;
  if (survey.createdById === user.id) return true;
  if (user.role && survey.editableRoles?.includes(user.role)) return true;
  const eg = survey.editableGroupIds || [];
  if (eg.length > 0 && myGroupIds.some((g) => eg.includes(g))) return true;
  return false;
}

interface SurveysPageProps {
  category?: SurveyCategory;
}

export function SurveysPage({ category = 'survey' }: SurveysPageProps) {
  const { confirm } = useConfirmDialog();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = category === 'evaluation' ? '/evaluations' : '/surveys';
  const platformPermissionKey =
    category === 'evaluation' ? 'assessments.evaluations.access' : 'assessments.surveys.access';
  const { canEdit: platformCanEdit, ready: platformPermsReady } = usePlatformAccessLevel(platformPermissionKey);
  const {
    items,
    loading,
    error,
    pagination,
    filters
  } = useSelector((state: RootState) => state.surveys);
  const user = useSelector(selectCurrentUser);
  const assessmentRecipientsState = useSelector((state: RootState) => state.assessmentRecipients);
  const [myGroupIds, setMyGroupIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const memberships = await fetchMyGroupMemberships();
        if (!cancelled) setMyGroupIds(memberships.map((m) => m.groupId));
      } catch {
        if (!cancelled) setMyGroupIds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canEditSurvey = useCallback(
    (survey: Survey) => canUserEditSurvey(survey, user, myGroupIds),
    [user, myGroupIds]
  );

  const canMutateSurvey = useCallback(
    (survey: Survey) => platformCanEdit && canEditSurvey(survey),
    [platformCanEdit, canEditSurvey]
  );

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Survey | null>(null);
  const [formError, setFormError] = useState<string>('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [generatingToken, setGeneratingToken] = useState(false);
  const [shareError, setShareError] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState<number | undefined>(undefined);

  const isCreatingNew = location.pathname === `${basePath}/new`;

  useEffect(() => {
    dispatch(setSurveysCategory(category));
  }, [dispatch, category]);

  useEffect(() => {
    dispatch(loadSurveys({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters.search,
      category,
      type: filters.type,
      active: filters.active,
      sortBy: filters.sortBy,
      sortDir: filters.sortDir,
    }));
  }, [dispatch, category, pagination.page, pagination.pageSize, filters.search, filters.type, filters.active, filters.sortBy, filters.sortDir]);

  // Controlar la visibilidad del formulario basado en la ruta (solo para creación nueva)
  useEffect(() => {
    if (isCreatingNew) {
      setShowForm(true);
      setEditingItem(null);
      setFormError('');
    } else if (!editingItem) {
      // Si no estamos en la ruta de "nuevo" y no estamos editando, ocultar el form
      setShowForm(false);
    }
  }, [isCreatingNew, editingItem]);

  useEffect(() => {
    if (platformPermsReady && !platformCanEdit && isCreatingNew) {
      navigate(basePath, { replace: true });
    }
  }, [platformPermsReady, platformCanEdit, isCreatingNew, navigate, basePath]);

  useEffect(() => {
    if (platformPermsReady && !platformCanEdit && editingItem) {
      setEditingItem(null);
      setShowForm(false);
      setFormError('');
    }
  }, [platformPermsReady, platformCanEdit, editingItem]);

  const buildPayload = (values: any): SurveyPayload => {
    const payload: SurveyPayload = {
      title: values.title,
      description: values.description,
      category,
      type: values.type,
      visibility: 'restricted' as const,
      visibleRoles: values.visibleRoles || [],
      editableRoles: values.editableRoles || [],
      visibleGroupIds: values.visibleGroupIds || [],
      editableGroupIds: values.editableGroupIds || [],
      periodType: values.periodType === null ? undefined : (values.periodType || undefined),
      seriesId: values.seriesId ?? undefined,
      periodLabel: values.periodLabel ?? undefined,
      periodDate: values.periodDate ?? undefined,
      threshold: values.threshold ?? undefined,
    };
    if (values.createdById) {
      (payload as any).createdById = values.createdById;
    }
    return payload;
  };

  const resetFormAndNavigate = () => {
    setShowForm(false);
    setEditingItem(null);
    navigate(basePath);
  };

  const handleSubmit = async (values: any) => {
    setFormError('');
    const payload = buildPayload(values);

    try {
      if (editingItem) {
        const result = await dispatch(editSurvey({ id: editingItem.id, payload }));
        if (!editSurvey.fulfilled.match(result)) {
          setFormError((result as any).error?.message || 'Error al actualizar la encuesta');
          return;
        }
        dispatch(clearCurrentSurvey());
      } else {
        const result = await dispatch(addSurvey(payload));
        if (!addSurvey.fulfilled.match(result)) {
          setFormError(result.error?.message || 'Error al crear la encuesta');
          return;
        }
        dispatch(loadSurveys({ page: 1, pageSize: pagination.pageSize, category, sortBy: 'createdAt', sortDir: 'desc' }));
      }
      resetFormAndNavigate();
    } catch (err: any) {
      setFormError(err.message || 'Ocurrió un error inesperado');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleEdit = (item: Survey) => {
    if (!platformCanEdit) {
      alert('No tienes permiso de plataforma para editar formularios');
      return;
    }
    // Normalizar para el formulario (mantener visibleRoles)
    const normalizedItem = { 
      ...item, 
      visibility: 'restricted' as const,
      visibleRoles: item.visibleRoles || [],
      editableRoles: item.editableRoles || [],
      visibleGroupIds: item.visibleGroupIds || [],
      editableGroupIds: item.editableGroupIds || [],
    };
    setEditingItem(normalizedItem);
    setFormError('');
    setShowForm(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleDelete = async (id: string) => {
    const survey = items.find(s => s.id === id);
    if (!survey || !canMutateSurvey(survey)) {
      alert('No tienes permisos para eliminar esta encuesta');
      return;
    }

    const ok = await confirm({
      title: 'Eliminar encuesta',
      message: '¿Estás seguro de que quieres eliminar esta encuesta?',
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      dispatch(removeSurvey(id));
    } catch (error) {
      console.error('Error deleting survey:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleViewResponses = (id: string) => {
    navigate(`${basePath}/${id}/responses`);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleManageForms = (id: string) => {
    navigate(`${basePath}/${id}/forms`);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleViewStatistics = (id: string) => {
    navigate(`${basePath}/${id}/statistics`);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleViewTeableSheet = (id: string) => {
    navigate(`${basePath}/${id}/sheet`);
  };

  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);

  // Load assessment recipients when share dialog opens with a selected survey
  useEffect(() => {
    if (showShareDialog && selectedSurvey) {
      dispatch(loadRecipients(selectedSurvey.id));
    }
  }, [showShareDialog, selectedSurvey, dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler estable; comportamiento preexistente
  const handleShareLink = (survey: Survey) => {
    setSelectedSurvey(survey);
    setShareLink('');
    setShareError('');
    setExpiresInDays(undefined);
    setShowShareDialog(true);
  };

  const handleGenerateToken = async () => {
    if (!selectedSurvey) return;

    if (!selectedSurvey.active) {
      setShareError('La encuesta debe estar activa para generar un link');
      return;
    }

    if (selectedSurvey.status !== 'published') {
      setShareError('La encuesta debe estar publicada para generar un link');
      return;
    }

    try {
      setGeneratingToken(true);
      setShareError('');
      const result = await generateSurveyToken(selectedSurvey.id, expiresInDays);
      const link = `${window.location.origin}/public/surveys/${result.token}`;
      setShareLink(link);
    } catch (error: any) {
      setShareError(error.message || 'Error al generar el link');
    } finally {
      setGeneratingToken(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      // Mostrar mensaje de éxito (podrías usar un toast aquí)
      alert('Link copiado al portapapeles');
    } catch {
      alert('No se pudo copiar el link. Cópielo manualmente: ' + shareLink);
    }
  };

  // --- Recipient handlers ---
  const handleAddRecipient = useCallback((externalRecipientId: string) => {
    if (!selectedSurvey) return;
    dispatch(addRecipient({ assessmentId: selectedSurvey.id, externalRecipientId }));
  }, [dispatch, selectedSurvey]);

  const handleRemoveRecipient = useCallback((recipientId: string) => {
    if (!selectedSurvey) return;
    dispatch(removeRecipient({ assessmentId: selectedSurvey.id, recipientId }));
  }, [dispatch, selectedSurvey]);

  const handleClearRecipients = useCallback(() => {
    if (!selectedSurvey) return;
    dispatch(clearRecipients(selectedSurvey.id));
  }, [dispatch, selectedSurvey]);

  const handleSendToAllRecipients = useCallback(() => {
    if (!selectedSurvey) return;
    void dispatch(generateAndSend(selectedSurvey.id));
  }, [dispatch, selectedSurvey]);

  const actionsCellRenderer = useCallback((info: any) => {
    const survey = info.row.original;
    return (
      <SurveyActionsDropdown
        survey={survey}
        onEdit={handleEdit}
        onManageForms={handleManageForms}
        onViewResponses={handleViewResponses}
        onViewStatistics={handleViewStatistics}
        onViewTeableSheet={handleViewTeableSheet}
        onShareLink={handleShareLink}
        onDelete={handleDelete}
        generatingToken={generatingToken}
        showShareLink={category === 'survey'}
        canEdit={canMutateSurvey(survey)}
      />
    );
  }, [handleEdit, handleManageForms, handleViewResponses, handleViewStatistics, handleViewTeableSheet, handleShareLink, handleDelete, generatingToken, category, canMutateSurvey]);

  const permissionsCellRenderer = useCallback((info: any) => (
    <SurveyPermissionsCell canEdit={canMutateSurvey(info.row.original)} />
  ), [canMutateSurvey]);

  const mappedColumns = useMemo(() => columns.map(col => {
    if (col.id === 'actions') return { ...col, cell: actionsCellRenderer };
    if (col.id === 'permissions') return { ...col, cell: permissionsCellRenderer };
    return col;
  }), [actionsCellRenderer, permissionsCellRenderer]);

  const isListView = !isCreatingNew && !editingItem && !showForm;

  if (isListView) {
    const activeFilterValue = filters.active === undefined ? '' : String(filters.active);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{category === 'evaluation' ? 'Evaluaciones' : 'Encuestas'}</h1>
          {platformCanEdit && (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`${basePath}/new`)}
            >
              {category === 'evaluation' ? 'Nueva Evaluación' : 'Nueva Encuesta'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Buscar encuestas</label>
            <input
              type="text"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
              placeholder="Buscar por título..."
              value={filters.search}
              onChange={(e) => dispatch(setSurveysSearch(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
              value={filters.type || ''}
              onChange={(e) => dispatch(setSurveysType(e.target.value === '' ? undefined : e.target.value as 'periodic' | 'permanent'))}
            >
              <option value="">Todos</option>
              <option value="periodic">Periódica</option>
              <option value="permanent">Permanente</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Estado</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
              value={activeFilterValue}
              onChange={(e) => dispatch(setSurveysActive(e.target.value === '' ? undefined : e.target.value === 'true'))}
            >
              <option value="">Todos</option>
              <option value="true">Activa</option>
              <option value="false">Inactiva</option>
            </select>
          </div>
        </div>

        <div>
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
              {error}
            </div>
          )}
          <DataTable
            columns={mappedColumns}
            data={items}
            loading={loading}
            emptyMessage="No hay encuestas registradas"
            pageSizeOptions={[10, 20, 50]}
          />
        </div>

        <ShareSurveyDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          selectedSurvey={selectedSurvey}
          shareLink={shareLink}
          shareError={shareError}
          generatingToken={generatingToken}
          expiresInDays={expiresInDays}
          onExpiresChange={setExpiresInDays}
          onGenerate={handleGenerateToken}
          onCopy={copyToClipboard}
          canGenerateToken={platformCanEdit}
          onClose={() => {
            setShowShareDialog(false);
            setShareLink('');
            setShareError('');
            setExpiresInDays(undefined);
            setSelectedSurvey(null);
          }}
          recipients={selectedSurvey ? (assessmentRecipientsState.items[selectedSurvey.id] || []) : []}
          recipientsLoading={assessmentRecipientsState.loading}
          recipientsError={assessmentRecipientsState.error || ''}
          onAddRecipient={handleAddRecipient}
          onRemoveRecipient={handleRemoveRecipient}
          onClearRecipients={handleClearRecipients}
          sendingSingle={assessmentRecipientsState.sendingSingle}
          sendSingleResult={assessmentRecipientsState.sendSingleResult}
          sendToAllLoading={assessmentRecipientsState.sending}
          onSendToAllRecipients={handleSendToAllRecipients}
        />
      </div>
    );
  }

  // Mostrar formulario cuando estamos creando o editando
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {editingItem ? 'Editar Encuesta' : 'Nueva Encuesta'}
        </h1>
        <button
          className="btn btn-secondary"
          onClick={() => {
            if (isCreatingNew) {
              navigate(basePath);
            } else {
              setShowForm(false);
              setEditingItem(null);
              setFormError('');
            }
          }}
        >
          ← Volver al listado
        </button>
      </div>

      <SurveysForm
        initialValues={editingItem ? {
          ...editingItem,
          visibleRoles: editingItem.visibleRoles || [],
          createdById: editingItem.createdById,
        } : null}
        onSubmit={handleSubmit}
        onCancel={() => {
          if (isCreatingNew) {
            navigate(basePath);
          } else {
            setShowForm(false);
            setEditingItem(null);
            setFormError('');
          }
        }}
        error={formError}
      />
    </div>
  );
}



